import { useState } from 'react';

import {
  LlmOutputPreferencesForm,
  SettingsListGroup,
  SettingsListRow,
  SettingsPageFrame,
} from '@eluve/blocks';
import type { DetailLevel, Format } from '@eluve/blocks';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  Button,
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  Icon,
  Separator,
  Text,
  useToast,
} from '@eluve/components';
import { getPreferenceLabel } from '@eluve/llm-outputs';

import {
  DEFAULT_DETAIL_LEVEL,
  DEFAULT_FORMAT,
  type PreferenceFormState,
  useChatSettings,
} from './useChatSettings';

export const ChatOutputPreferencesPage = () => {
  const { preferences, savePreference, deletePreferenceWithUnlink } =
    useChatSettings();
  const toast = useToast();

  const [editingPreference, setEditingPreference] =
    useState<PreferenceFormState | null>(null);
  const [showDeletePrefDialog, setShowDeletePrefDialog] = useState<
    string | null
  >(null);

  const handleCreatePreference = () => {
    setEditingPreference({
      id: '',
      name: '',
      format: DEFAULT_FORMAT,
      detailLevel: DEFAULT_DETAIL_LEVEL,
      customInstructions: '',
      isNew: true,
    });
  };

  const handleEditPreference = (prefId: string) => {
    const pref = preferences.find((p) => p.id === prefId);
    if (!pref) return;
    setEditingPreference({
      id: pref.id,
      name: pref.name ?? '',
      format: (pref.format as Format) ?? DEFAULT_FORMAT,
      detailLevel: (pref.detailLevel as DetailLevel) ?? DEFAULT_DETAIL_LEVEL,
      customInstructions: pref.customInstructions ?? '',
    });
  };

  const handleSavePreference = async () => {
    if (!editingPreference) return;
    try {
      await savePreference(editingPreference);
      setEditingPreference(null);
    } catch {
      toast.error('Failed to save preference');
    }
  };

  const handleDeletePreference = async (prefId: string) => {
    try {
      await deletePreferenceWithUnlink(prefId);
      setShowDeletePrefDialog(null);
    } catch {
      toast.error('Failed to delete preference');
    }
  };

  return (
    <SettingsPageFrame
      title="Output Preferences"
      parentTitle="Chat Preferences"
      backTo=".."
      description="Configure response format, detail level, and custom instructions. Link them to conversation starters."
    >
      <SettingsListGroup
        countLabel={`${preferences.length} ${preferences.length === 1 ? 'preference' : 'preferences'}`}
        actions={
          <Button variant="outline" size="sm" onClick={handleCreatePreference}>
            <Icon name="Plus" size="auto" />
            Add
          </Button>
        }
      >
        {preferences.length > 0 ? (
          preferences.map((pref, i) => (
            <div key={pref.id}>
              <SettingsListRow
                editLabel={`Edit ${getPreferenceLabel(pref)}`}
                deleteLabel={`Delete ${getPreferenceLabel(pref)}`}
                onEdit={() => handleEditPreference(pref.id)}
                onDelete={() => setShowDeletePrefDialog(pref.id)}
              >
                <Text inline size="sm" weight="semibold">
                  {getPreferenceLabel(pref)}
                </Text>
              </SettingsListRow>
              {i < preferences.length - 1 && <Separator className="my-0" />}
            </div>
          ))
        ) : (
          <Text size="sm" color="tertiary" className="px-6 py-7">
            No output preferences configured
          </Text>
        )}
      </SettingsListGroup>

      <Dialog
        open={editingPreference !== null}
        onOpenChange={(open) => {
          if (!open) setEditingPreference(null);
        }}
      >
        <DialogContent size="xl" showCloseButton={false}>
          <DialogHeader>
            <DialogTitle>
              {editingPreference?.isNew
                ? 'New Output Preference'
                : 'Edit Output Preference'}
            </DialogTitle>
          </DialogHeader>
          {editingPreference && (
            <LlmOutputPreferencesForm
              name={editingPreference.name}
              onNameChange={(name) =>
                setEditingPreference((prev) =>
                  prev ? { ...prev, name } : null,
                )
              }
              format={editingPreference.format}
              detailLevel={editingPreference.detailLevel}
              customInstructions={editingPreference.customInstructions}
              onFormatChange={(format) =>
                setEditingPreference((prev) =>
                  prev ? { ...prev, format } : null,
                )
              }
              onDetailLevelChange={(detailLevel) =>
                setEditingPreference((prev) =>
                  prev ? { ...prev, detailLevel } : null,
                )
              }
              onCustomInstructionsChange={(customInstructions) =>
                setEditingPreference((prev) =>
                  prev ? { ...prev, customInstructions } : null,
                )
              }
            />
          )}
          <DialogFooter>
            <Button
              variant="ghost"
              size="sm"
              onClick={() => setEditingPreference(null)}
            >
              Cancel
            </Button>
            <Button variant="outline" size="sm" onClick={handleSavePreference}>
              Save
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <AlertDialog
        open={showDeletePrefDialog !== null}
        onOpenChange={(open) => {
          if (!open) setShowDeletePrefDialog(null);
        }}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Preference?</AlertDialogTitle>
            <AlertDialogDescription>
              This will delete this output preference. Any conversation starters
              linked to it will be unlinked. This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => {
                if (showDeletePrefDialog) {
                  handleDeletePreference(showDeletePrefDialog);
                }
              }}
            >
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </SettingsPageFrame>
  );
};
